import { AxiosRequestConfig } from "axios";
import { CohesiveAPIClient } from "../../client";
import { route } from "../../utils";
import { TranscriptionRequest, TranscriptionResponse } from "./types";

export * from "./types";

const ENDPOINTS = {
  TRANSCRIBE_AUDIO:
    "/api/v2/org/:organization_id/user/:user_id/audio-transcription",
};

/**
 * Pick an upload filename whose extension matches the blob's MIME type.
 *
 * The backend infers the audio format from the file **extension**, so a
 * hardcoded name is wrong for anything but webm. iOS Safari / WKWebView records
 * `audio/mp4` (not webm), which previously broke dictation on iPhone.
 */
function audioFilenameForBlob(blob: Blob): string {
  const mime = (blob.type || "").split(";")[0].trim().toLowerCase();
  const extByMime: Record<string, string> = {
    "audio/webm": "webm",
    "video/webm": "webm",
    "audio/mp4": "mp4",
    "audio/m4a": "m4a",
    "audio/x-m4a": "m4a",
    "audio/aac": "aac",
    "audio/mpeg": "mp3",
    "audio/mp3": "mp3",
    "audio/wav": "wav",
    "audio/x-wav": "wav",
    "audio/ogg": "ogg",
  };
  return `recording.${extByMime[mime] ?? "webm"}`;
}

export class AudioTranscriptionAPI {
  constructor(private client: CohesiveAPIClient) {}

  async transcribeAudio(
    audioBlob: Blob,
    options?: Omit<TranscriptionRequest, "file">,
    config?: AxiosRequestConfig,
  ): Promise<TranscriptionResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    // Create FormData for file upload
    const formData = new FormData();

    // Create a file from the blob. The filename extension must match the blob's
    // MIME type — the backend derives the audio format from it.
    const audioFile = new File([audioBlob], audioFilenameForBlob(audioBlob), {
      type: audioBlob.type,
    });

    formData.append("file", audioFile);

    if (!options?.language) {
      formData.append("language", "en");
    }

    // Add optional parameters
    if (options?.model) {
      formData.append("model", options.model);
    } else {
      formData.append("model", "gpt-4o-transcribe");
    }

    if (options?.language) {
      formData.append("language", options.language);
    }

    if (options?.prompt) {
      formData.append("prompt", options.prompt);
    }

    if (options?.response_format) {
      formData.append("response_format", options.response_format);
    } else {
      formData.append("response_format", "json");
    }

    if (options?.temperature !== undefined) {
      formData.append("temperature", options.temperature.toString());
    }

    if (options?.timestamp_granularities) {
      options.timestamp_granularities.forEach((granularity) => {
        formData.append("timestamp_granularities[]", granularity);
      });
    }

    const response = await this.client.axios.post<TranscriptionResponse>(
      route(ENDPOINTS.TRANSCRIBE_AUDIO, {
        organization_id: organizationId,
        user_id: userId,
      }),
      formData,
      {
        ...config,
        headers: {
          ...config?.headers,
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return response.data;
  }
}
